import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert, Empty, Modal, Spinner } from '../components/ui.tsx'
import { api, type User } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useAction, useAsync } from '../lib/hooks.ts'
import { formatDate } from '../lib/format.ts'

export function ClientsPage(): React.JSX.Element {
  const [creating, setCreating] = useState(false)
  const clients = useAsync(() => api.users.list('client'), [])

  const list = clients.data?.users ?? []

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>Klienti</h1>
          <p>Lidé, kterým nastavuješ tréninkové programy.</p>
        </div>
        <div className="page__actions">
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            Nový klient
          </button>
        </div>
      </div>

      {clients.error && <Alert>{clients.error}</Alert>}
      {clients.loading && <Spinner />}

      {clients.data && list.length === 0 && (
        <Empty title="Zatím žádní klienti">
          <p>Založ prvního klienta a přiřaď mu program.</p>
        </Empty>
      )}

      {list.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Jméno</th>
                <th>E-mail</th>
                <th>Stav</th>
                <th>Založen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((client) => (
                <tr key={client.id}>
                  <td>
                    <Link to={`/klienti/${client.id}`}>{client.name}</Link>
                  </td>
                  <td className="muted">{client.email}</td>
                  <td>
                    <span className={`badge ${client.active ? 'badge--success' : ''}`}>
                      {client.active ? 'aktivní' : 'deaktivovaný'}
                    </span>
                  </td>
                  <td className="muted">{formatDate(client.createdAt)}</td>
                  <td>
                    <Link to={`/klienti/${client.id}`} className="btn btn--ghost btn--sm">
                      Otevřít
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewClientModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            clients.reload()
          }}
        />
      )}
    </main>
  )
}

/** Založení klienta. Heslo zadává trenér a předá ho klientovi osobně. */
export function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (user: User) => void
}): React.JSX.Element {
  const { user } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState(suggestPassword())
  const [note, setNote] = useState('')
  const [coachId, setCoachId] = useState<number | ''>('')

  // Admin nemá klienty pod sebou, proto si volí, komu klienta přidělí.
  const coaches = useAsync(
    async () => (user?.role === 'admin' ? api.users.coaches() : { users: [] }),
    [user?.role],
  )

  const create = useAction(api.users.create)

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    const result = await create.run({
      name,
      email,
      password,
      role: 'client',
      coachId: coachId === '' ? null : coachId,
      note: note || null,
    })
    if (result) onCreated(result.user)
  }

  return (
    <Modal
      title="Nový klient"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button
            type="submit"
            form="new-client"
            className="btn btn--primary"
            disabled={create.pending}
          >
            {create.pending ? 'Zakládám…' : 'Založit'}
          </button>
        </>
      }
    >
      <form id="new-client" onSubmit={(e) => void submit(e)}>
        {create.error && <Alert>{create.error}</Alert>}

        <label className="field">
          <span className="field__label">Jméno a příjmení</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          <span className="field__label">E-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <span className="field__help">Slouží zároveň jako přihlašovací jméno.</span>
        </label>

        <label className="field">
          <span className="field__label">Počáteční heslo</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <span className="field__help">
            Předej ho klientovi osobně. V Nastavení si ho pak může sám změnit.
          </span>
        </label>

        {user?.role === 'admin' && (
          <label className="field">
            <span className="field__label">Trenér</span>
            <select value={coachId} onChange={(e) => setCoachId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— bez trenéra —</option>
              {(coaches.data?.users ?? []).map((coach) => (
                <option key={coach.id} value={coach.id}>
                  {coach.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span className="field__label">Poznámka</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Sport, cíl tréninku, zdravotní omezení…"
          />
        </label>
      </form>
    </Modal>
  )
}

/** Návrh hesla, které jde nadiktovat po telefonu — bez zaměnitelných znaků. */
function suggestPassword(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789'
  const bytes = crypto.getRandomValues(new Uint8Array(10))
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}
